import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiTodayFollowups, apiUpdateFollowup } from '../services/api';
import toast from 'react-hot-toast';

// Note: apiTodayFollowups returns only Pending follow-ups for today

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

export default function FollowupPage() {
    const navigate = useNavigate();
    const [followups, setFollowups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiTodayFollowups();
            setFollowups(res.data?.data || []);
        } catch {
            toast.error('Failed to load follow-ups');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const markDone = async (fu) => {
        setUpdating(fu.id);
        try {
            await apiUpdateFollowup(fu.id, {
                followup_date: fu.followup_date?.split('T')[0] || fu.followup_date,
                notes: fu.notes,
                status: 'Done',
            });
            toast.success('Follow-up marked as done!');
            load();
        } catch {
            toast.error('Update failed');
        } finally {
            setUpdating(null);
        }
    };

    const pending = followups.filter(f => f.status === 'Pending');
    const done    = followups.filter(f => f.status === 'Done');

    return (
        <div className="page">
            <div className="page-header">
                <div>
                    <h1 className="page-title">Today's Follow-ups</h1>
                    <p className="page-subtitle">
                        {pending.length} pending · {done.length} completed — {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                    </p>
                </div>
                <button className="btn btn-ghost" onClick={load}>🔄 Refresh</button>
            </div>

            {/* Summary strip */}
            <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
                <div className="stat-card stat-card--orange" style={{ flex: 1, minWidth: 160 }}>
                    <div className="stat-icon">⏳</div>
                    <div><span className="stat-value">{pending.length}</span><span className="stat-label">Pending</span></div>
                </div>
                <div className="stat-card stat-card--green" style={{ flex: 1, minWidth: 160 }}>
                    <div className="stat-icon">✅</div>
                    <div><span className="stat-value">{done.length}</span><span className="stat-label">Done Today</span></div>
                </div>
                <div className="stat-card stat-card--blue" style={{ flex: 1, minWidth: 160 }}>
                    <div className="stat-icon">📅</div>
                    <div><span className="stat-value">{followups.length}</span><span className="stat-label">Total Due Today</span></div>
                </div>
            </div>

            {loading ? (
                <div className="loading-center"><div className="spinner" /><span>Loading…</span></div>
            ) : followups.length === 0 ? (
                <div className="card">
                    <div className="empty-state">
                        <div className="empty-state-icon">🎉</div>
                        <p>No follow-ups today — you're all caught up!</p>
                    </div>
                </div>
            ) : (
                <>
                    {/* Pending */}
                    {pending.length > 0 && (
                        <div className="card" style={{ marginBottom: 20 }}>
                            <div className="card-header">
                                <span className="card-title">⏳ Pending</span>
                                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{pending.length} remaining</span>
                            </div>
                            <div className="table-wrapper">
                                <table className="data-table" id="pending-followups-table">
                                    <thead>
                                        <tr>
                                            <th>Lead Name</th>
                                            <th>Phone</th>
                                            <th>Course</th>
                                            <th>Notes</th>
                                            <th>Date</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pending.map(fu => (
                                            <tr key={fu.id} id={`followup-row-${fu.id}`}>
                                                <td>
                                                    <strong
                                                        style={{ color: 'var(--accent-light)', cursor: 'pointer' }}
                                                        onClick={() => navigate(`/leads/${fu.lead_id}`)}
                                                    >
                                                        {fu.lead_name || '—'}
                                                    </strong>
                                                </td>
                                                <td>{fu.lead_phone || '—'}</td>
                                                <td>{fu.lead_course || '—'}</td>
                                                <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {fu.notes || '—'}
                                                </td>
                                                <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(fu.followup_date)}</td>
                                                <td>
                                                    <div className="action-cell">
                                                        <button
                                                            id={`mark-done-${fu.id}`}
                                                            className="btn btn-success btn-sm"
                                                            disabled={updating === fu.id}
                                                            onClick={() => markDone(fu)}
                                                        >
                                                            {updating === fu.id ? '…' : '✔ Done'}
                                                        </button>
                                                        <button
                                                            className="btn btn-ghost btn-sm btn-icon"
                                                            title="View Lead"
                                                            onClick={() => navigate(`/leads/${fu.lead_id}`)}
                                                        >👁</button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Done */}
                    {done.length > 0 && (
                        <div className="card">
                            <div className="card-header">
                                <span className="card-title">✅ Completed Today</span>
                            </div>
                            <div className="table-wrapper">
                                <table className="data-table" id="done-followups-table">
                                    <thead>
                                        <tr>
                                            <th>Lead Name</th>
                                            <th>Phone</th>
                                            <th>Notes</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {done.map(fu => (
                                            <tr key={fu.id} style={{ opacity: 0.65 }}>
                                                <td><strong>{fu.lead_name || '—'}</strong></td>
                                                <td>{fu.lead_phone || '—'}</td>
                                                <td>{fu.notes || '—'}</td>
                                                <td>
                                                    <button className="btn btn-ghost btn-sm btn-icon" title="View Lead" onClick={() => navigate(`/leads/${fu.lead_id}`)}>👁</button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
